import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";
import FlagIcon from "@mui/icons-material/Flag";
import AxiosUrl from "../../AxiosUrl";
import { toast } from "react-toastify";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./ViewCVModal.css";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WcIcon from "@mui/icons-material/Wc";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HeightIcon from "@mui/icons-material/Height";
import PublicIcon from "@mui/icons-material/Public";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PeopleIcon from "@mui/icons-material/People";
import TranslateIcon from "@mui/icons-material/Translate";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";


function ViewCVModal({ open, onClose, userId, userName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [editedCvData, setEditedCvData] = useState(null);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  const isMobile = useMediaQuery("(max-width:600px)");
  const uid = localStorage.getItem("id");

  useEffect(() => {
    if (open && userId) {
      fetchCVData();
    }
  }, [open, userId]);

  // Function to format CV data from any source into a consistent structure for the UI
  const formatCvDataForUI = (sourceData) => {
    console.log("Formatting CV data for UI:", sourceData);
    
    // Extract height feet and inches if they exist as separate fields or from the height string
    let heightFeet = sourceData.heightFeet || sourceData.personal?.heightFeet || "";
    let heightInches = sourceData.heightInches || sourceData.personal?.heightInches || "";
    
    // If we don't have separate height fields but have a formatted height string
    const heightStr = sourceData.height || sourceData.personal?.height || "";
    if (!heightFeet && heightStr) {
      const heightMatch = heightStr.match(/(\d+)'(?:\s*(\d+)")?/);
      if (heightMatch) {
        heightFeet = heightMatch[1] || "";
        heightInches = heightMatch[2] || "";
      }
    }
    
    return {
      personal: {
        age: sourceData.age || sourceData.personal?.age || "",
        gender: sourceData.gender || sourceData.personal?.gender || "",
        heightFeet: heightFeet,
        heightInches: heightInches,
        maritalStatus: sourceData.maritalStatus || sourceData.personal?.maritalStatus || "",
        numberOfChildren: sourceData.numberOfChildren || sourceData.personal?.numberOfChildren || "0",
        country: sourceData.country || sourceData.personal?.country || "",
        city: sourceData.city || sourceData.personal?.city || "",
        subCity: sourceData.subCity || sourceData.personal?.subCity || "",
        citizenship: sourceData.citizenship || sourceData.personal?.citizenship || "",
        religion: sourceData.religion || sourceData.personal?.religion || "",
        annualIncome: sourceData.annualIncome || sourceData.personal?.annualIncome || ""
      },
      education: {
        degree: sourceData.education?.degree || sourceData.education || ""
      },
      work: {
        employedIn: sourceData.employedIn || sourceData.work?.employedIn || "",
        occupation: sourceData.occupation || sourceData.work?.occupation || ""
      },
      family: {
        numberOfBrothers: sourceData.numberOfBrothers || sourceData.family?.numberOfBrothers || "0",
        fathersOccupation: sourceData.fathersOccupation || sourceData.family?.fathersOccupation || "",
        mothersOccupation: sourceData.mothersOccupation || sourceData.family?.mothersOccupation || "",
        numberOfSisters: sourceData.numberOfSisters || sourceData.family?.numberOfSisters || "0"
      },
      languages: sourceData.languagesKnown || sourceData.languages || []
    };
  };

  const fetchCVData = () => {
    setLoading(true);
    setError(null);
    setEditMode(false);

    AxiosUrl.post(`/fetch-cv/${uid}`, { userId: userId })
      .then((res) => {        
        if (res.data.success && res.data.cv) {
          const cvDataFromServer = res.data.cv;
          
          // Store the raw CV data for reference regardless of format
          setCvData(cvDataFromServer);
          
          // Check data format and format accordingly using our helper function
          const formattedData = formatCvDataForUI(cvDataFromServer);
          
          // Initialize edited data with the formatted data
          setEditedCvData(formattedData);
        } else {
          setError(res.data.error || "Failed to load CV data");
          toast.error(res.data.error || "Failed to load CV data");
        }
      })
      .catch((err) => {
        console.error("Error fetching CV:", err);
        setError("Failed to load CV data");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toggleEditMode = () => {
    if (editMode) {
      // If exiting edit mode, reset the edited data to the original data
      setEditedCvData(formatCvDataForUI(cvData));
      setEditMode(false);
    } else {
      // If entering edit mode, ensure editedCvData is properly initialized with the latest data
      setEditedCvData(formatCvDataForUI(cvData));
      setEditMode(true);
    }
  };

  // Function to handle field value changes
  const handleFieldChange = (section, field, value) => {
    setEditedCvData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Function to save edits back to server
  const saveChanges = () => {
    setLoading(true);
    setError(null);
    
    // Track which fields were actually changed
    const changedFields = {};
    const originalData = cvData;
    
    // Helper function to check if a value has changed
    const hasChanged = (newVal, oldVal) => {
      if (Array.isArray(newVal) && Array.isArray(oldVal)) {
        // Compare arrays (for languages)
        if (newVal.length !== oldVal.length) return true;
        return !newVal.every(item => oldVal.includes(item));
      }
      
      // Compare regular values, handling null/undefined/empty strings
      const oldValue = oldVal === null || oldVal === undefined ? "" : oldVal;
      const newValue = newVal === null || newVal === undefined ? "" : newVal;
      return String(newValue) !== String(oldValue);
    };
    
    // Check personal fields
    const personalFields = [
      'age', 'gender', 'maritalStatus', 'numberOfChildren', 
      'country', 'city', 'subCity', 'citizenship', 'religion', 'annualIncome'
    ];
    
    personalFields.forEach(field => {
      const newValue = editedCvData.personal?.[field];
      const oldValue = originalData[field] || originalData.personal?.[field];
      
      if (hasChanged(newValue, oldValue)) {
        changedFields[field] = newValue;
      }
    });
    
    // Handle height specially (it's stored as two fields but may be displayed as one)
    const newHeightFeet = editedCvData.personal?.heightFeet;
    const newHeightInches = editedCvData.personal?.heightInches;
    const oldHeightFeet = originalData.heightFeet || originalData.personal?.heightFeet;
    const oldHeightInches = originalData.heightInches || originalData.personal?.heightInches;
    
    if (hasChanged(newHeightFeet, oldHeightFeet)) {
      changedFields.heightFeet = newHeightFeet;
    }
    
    if (hasChanged(newHeightInches, oldHeightInches)) {
      changedFields.heightInches = newHeightInches;
    }
    
    // Check education
    if (hasChanged(editedCvData.education?.degree, originalData.education || originalData.education?.degree)) {
      changedFields.education = editedCvData.education?.degree;
    }
    
    // Check work fields
    if (hasChanged(editedCvData.work?.employedIn, originalData.employedIn || originalData.work?.employedIn)) {
      changedFields.employedIn = editedCvData.work?.employedIn;
    }
    
    if (hasChanged(editedCvData.work?.occupation, originalData.occupation || originalData.work?.occupation)) {
      changedFields.occupation = editedCvData.work?.occupation;
    }
    
    // Check family fields
    if (hasChanged(editedCvData.family?.numberOfBrothers, originalData.numberOfBrothers || originalData.family?.numberOfBrothers)) {
      changedFields.numberOfBrothers = editedCvData.family?.numberOfBrothers;
    }
    
    if (hasChanged(editedCvData.family?.numberOfSisters, originalData.numberOfSisters || originalData.family?.numberOfSisters)) {
      changedFields.numberOfSisters = editedCvData.family?.numberOfSisters;
    }
    
    if (hasChanged(editedCvData.family?.fathersOccupation, originalData.fathersOccupation || originalData.family?.fathersOccupation)) {
      changedFields.fathersOccupation = editedCvData.family?.fathersOccupation;
    }
    
    if (hasChanged(editedCvData.family?.mothersOccupation, originalData.mothersOccupation || originalData.family?.mothersOccupation)) {
      changedFields.mothersOccupation = editedCvData.family?.mothersOccupation;
    }
    
    // Check languages
    if (hasChanged(editedCvData.languages, originalData.languagesKnown || originalData.languages)) {
      changedFields.languagesKnown = editedCvData.languages;
    }
    
    // Don't send request if nothing has changed
    if (Object.keys(changedFields).length === 0) {
      setLoading(false);
      toast.info("No changes were made");
      setEditMode(false);
      return;
    }
    
    // Use the correct endpoint for updating CV
    AxiosUrl.post(`/update-cv/${uid}`, { 
      userId: userId,
      cv: changedFields
    })
      .then((res) => {
        if (res.data.success) {
          setEditMode(false);
          toast.success("CV updated successfully");
          // Refetch the CV data to get the updated version
          fetchCVData(); 
        } else {
          setError(res.data.error || "Failed to update CV");
          toast.error(res.data.error || "Failed to update CV");
        }
      })
      .catch((err) => {
        console.error("Error updating CV:", err);
        setError("Failed to update CV");
        toast.error("Failed to update CV");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Helper function to render select fields with appropriate options
  const renderSelectForField = (section, field, value) => {
    let options = [];
    
    // Define options based on the field
    if (section === 'personal' && field === 'gender') {
      options = [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" }
      ];
    } else if (section === 'personal' && field === 'maritalStatus') {
      options = [
        { value: "Single", label: "Single" },
        { value: "Divorced", label: "Divorced" },
        { value: "Widowed", label: "Widowed" },
        { value: "Separated", label: "Separated" }
      ];
    } else if (section === 'work' && field === 'employedIn') {
      options = [
        { value: "Private", label: "Private Company" },
        { value: "Government", label: "Government / Public Sector" },
        { value: "Business", label: "Business / Self Employed" },
        { value: "Not Working", label: "Not Working" }
      ];
    }
    
    return (
      <select
        value={value}
        onChange={(e) => handleFieldChange(section, field, e.target.value)}
        className="edit-field-select"
      >
        <option value="">Select {field}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  // Special rendering for height field which has feet and inches
  const renderHeightField = () => {
    const heightFeet = editedCvData.personal?.heightFeet || '';
    const heightInches = editedCvData.personal?.heightInches || '';
    
    console.log("Rendering height field with values:", heightFeet, heightInches);
    
    return (
      <div className="view-cv-field">
        <div className="field-label">Height:</div>
        <div className="field-value">
          {editMode ? (
            <div className="height-input-container">
              <input
                type="number"
                min="0"
                max="8"
                value={heightFeet}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditedCvData(prev => ({
                    ...prev,
                    personal: {
                      ...prev.personal,
                      heightFeet: value
                    }
                  }));
                }}
                className="height-input feet"
                placeholder="ft"
              />
              <span className="height-separator">'</span>
              <input
                type="number"
                min="0"
                max="11"
                value={heightInches}
                onChange={(e) => {
                  const value = e.target.value;
                  setEditedCvData(prev => ({
                    ...prev,
                    personal: {
                      ...prev.personal,
                      heightInches: value
                    }
                  }));
                }}
                className="height-input inches"
                placeholder="in"
              />
              <span className="height-separator">"</span>
            </div>
          ) : (
            <span>
              {heightFeet || cvData.heightFeet ? 
                `${heightFeet || cvData.heightFeet}'${heightInches || cvData.heightInches ? ` ${heightInches || cvData.heightInches}"` : ''}` : 
                'Not specified'}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render a field based on whether we're in edit mode or not
  const renderField = (label, section, field, inputType = "text") => {
    const fieldValue = editedCvData[section]?.[field] || "";
    console.log(`Rendering field ${section}.${field}, value:`, fieldValue);
    
    return (
      <div className="view-cv-field">
        <div className="field-label">{label}:</div>
        <div className="field-value">
          {editMode ? (
            inputType === "select" ? (
              renderSelectForField(section, field, fieldValue)
            ) : (
              <input
                type={inputType}
                value={fieldValue}
                onChange={(e) => handleFieldChange(section, field, e.target.value)}
                className="edit-field-input"
              />
            )
          ) : (
            <span>{fieldValue || "Not specified"}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={(loading || saving) ? null : onClose}
      aria-labelledby="view-cv-modal-title"
      aria-describedby="view-cv-modal-description"
    >
      <Box className={`view-cv-modal ${isMobile ? 'view-cv-modal-mobile' : ''}`}>
        <div className="view-cv-modal-header">
          <Typography variant="h6" component="h2" className="view-cv-modal-title">
            <DescriptionIcon className="view-cv-modal-title-icon" />
            {userName ? `${userName}'s CV` : 'Curriculum Vitae'}
          </Typography>
          <div className="view-cv-modal-actions">
            {!loading && !saving && cvData && (
              <>
                {editMode ? (
                  <Button 
                    variant="contained" 
                    color="success" 
                    onClick={saveChanges} 
                    startIcon={<SaveIcon />}
                    disabled={loading || saving}
                  >
                    Save Changes
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={toggleEditMode}
                    startIcon={<EditIcon />}
                    disabled={loading}
                  >
                    Edit CV
                  </Button>
                )}
              </>
            )}
            {!loading && !saving && (
              <Button 
                variant="contained" 
                className="close-modal-button"
                onClick={onClose}
              >
                Close Modal
              </Button>
            )}
          </div>
        </div>

        <div className="view-cv-modal-content">
          {loading ? (
            <div className="view-cv-modal-loading">
              <CircularProgress size={50} color="primary" />
              <Typography className="view-cv-modal-loading-text">
                Loading CV data...
              </Typography>
            </div>
          ) : saving ? (
            <div className="view-cv-modal-loading">
              <CircularProgress size={50} color="primary" />
              <Typography className="view-cv-modal-loading-text">
                Saving changes...
              </Typography>
            </div>
          ) : error ? (
            <div className="view-cv-modal-error">
              <Typography className="view-cv-modal-error-text">
                {error}
              </Typography>
              <Button 
                onClick={fetchCVData} 
                className="view-cv-modal-retry-button"
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            </div>
          ) : cvData ? (
            <div className="view-cv-modal-data">
              {/* Personal Information Section */}
              <div className="view-cv-section">
                <div className="view-cv-section-header">
                  <PersonIcon className="view-cv-section-icon" />
                  <Typography className="view-cv-section-title">
                    Personal Information
                  </Typography>
                </div>
                <div className="view-cv-section-content">
                  <div className="view-cv-info-grid">
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PersonIcon fontSize="small" /> Full Name:</span>
                      <span className="view-cv-info-value">{userName}</span>
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><CalendarTodayIcon fontSize="small" /> Age:</span>
                      {renderField("Age", "personal", "age", "number")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><WcIcon fontSize="small" /> Gender:</span>
                      {renderField("Gender", "personal", "gender", "select")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><HeightIcon fontSize="small" /> Height:</span>
                      {renderHeightField()}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><FavoriteIcon fontSize="small" /> Marital Status:</span>
                      {renderField("Marital Status", "personal", "maritalStatus", "select")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><FlagIcon fontSize="small" /> Religion:</span>
                      {renderField("Religion", "personal", "religion")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PublicIcon fontSize="small" /> Citizenship:</span>
                      {renderField("Citizenship", "personal", "citizenship")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><MonetizationOnIcon fontSize="small" /> Annual Income:</span>
                      {renderField("Annual Income", "personal", "annualIncome", "number")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="view-cv-section">
                <div className="view-cv-section-header">
                  <LocationOnIcon className="view-cv-section-icon" />
                  <Typography className="view-cv-section-title">
                    Location Information
                  </Typography>
                </div>
                <div className="view-cv-section-content">
                  <div className="view-cv-info-grid">
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PublicIcon fontSize="small" /> Country:</span>
                      {renderField("Country", "personal", "country")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><LocationCityIcon fontSize="small" /> City:</span>
                      {renderField("City", "personal", "city")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><HomeIcon fontSize="small" /> Sub City/Area:</span>
                      {renderField("Sub City/Area", "personal", "subCity")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Education & Work Section */}
              <div className="view-cv-section">
                <div className="view-cv-section-header">
                  <WorkIcon className="view-cv-section-icon" />
                  <Typography className="view-cv-section-title">
                    Education & Work
                  </Typography>
                </div>
                <div className="view-cv-section-content">
                  <div className="view-cv-info-grid">
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><SchoolIcon fontSize="small" /> Education:</span>
                      {renderField("Education/Degree", "education", "degree")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><WorkIcon fontSize="small" /> Occupation:</span>
                      {renderField("Occupation", "work", "occupation")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><BusinessIcon fontSize="small" /> Employed In:</span>
                      {renderField("Employed In", "work", "employedIn", "select")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Family Information */}
              <div className="view-cv-section">
                <div className="view-cv-section-header">
                  <FamilyRestroomIcon className="view-cv-section-icon" />
                  <Typography className="view-cv-section-title">
                    Family Information
                  </Typography>
                </div>
                <div className="view-cv-section-content">
                  <div className="view-cv-info-grid">
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PersonIcon fontSize="small" /> Father's Occupation:</span>
                      {renderField("Father's Occupation", "family", "fathersOccupation")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PersonIcon fontSize="small" /> Mother's Occupation:</span>
                      {renderField("Mother's Occupation", "family", "mothersOccupation")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PeopleIcon fontSize="small" /> Number of Brothers:</span>
                      {renderField("Number of Brothers", "family", "numberOfBrothers", "number")}
                    </div>
                    <div className="view-cv-info-item">
                      <span className="view-cv-info-label"><PeopleIcon fontSize="small" /> Number of Sisters:</span>
                      {renderField("Number of Sisters", "family", "numberOfSisters", "number")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Languages Section */}
              {cvData.languages && cvData.languages.length > 0 && (
                <div className="view-cv-section">
                  <div className="view-cv-section-header">
                    <LanguageIcon className="view-cv-section-icon" />
                    <Typography className="view-cv-section-title">
                      Languages
                    </Typography>
                  </div>
                  <div className="view-cv-section-content">
                    <div className="view-cv-skills-languages">
                      <div className="view-cv-languages">
                        <div className="view-cv-languages-list">
                          {cvData.languages.map((language, index) => (
                            <span key={index} className="view-cv-language-item">
                              <TranslateIcon fontSize="small" style={{ marginRight: '5px' }} />
                              {language}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Box>
    </Modal>
  );
}

export default ViewCVModal; 